import { useState, type FormEvent } from 'react'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { api, type ApiError } from '../api/client'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'

// POST /auth/login lands with DIS-27; the form is wired to it already.
export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof s.redirect === 'string' ? s.redirect : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    if (await context.auth.me()) {
      throw redirect({ to: search.redirect ?? '/' })
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const { auth } = Route.useRouteContext()
  const { redirect: target } = Route.useSearch()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const form = new FormData(e.currentTarget)
    const { data, error: err, response } = await api.POST('/auth/login', {
      body: { email: String(form.get('email') ?? ''), password: String(form.get('password') ?? '') },
    })
    setBusy(false)
    if (data) {
      await auth.invalidate()
      await navigate({ to: target ?? '/' })
      return
    }
    setError((err as ApiError | undefined)?.message ?? `Sign-in failed (${response.status})`)
  }

  return (
    <div className="flex h-full items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-bg-muted p-6">
        <div>
          <h1 className="text-lg font-semibold">Sign in to Dispatch</h1>
        </div>
        <Input name="email" type="email" required placeholder="you@example.com" autoComplete="username" autoFocus />
        <Input name="password" type="password" required placeholder="Password" autoComplete="current-password" />
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
        <p className="text-center text-sm">
          <Link to="/forgot" className="text-fg-muted underline hover:text-fg">
            Forgot your password?
          </Link>
        </p>
      </form>
    </div>
  )
}
